import { AnimatePresence } from 'motion/react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { PairLink } from '@root/entities/pair/ui/common/pair-link'
import { AppDataTable } from '@root/features/app-data-table/app-data-table'
import { reatomProfilePositionsTable } from '@root/features/trade/position/profile-positions-table/atoms'
import { WalletPositionsDialog } from '@root/features/trade/position/wallet-positions-dialog/ui'
import { PageSection } from '@root/shared/ui-kit'

import type { PositionTableColumnId } from '../../positions-table/atoms'
import type {
  AggregatedPositionModel,
  WalletPositionModel,
} from '@root/entities/position'
import type { DelugeId } from '@root/entities/user-short'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  delugeId: DelugeId
} & ClassNameProps

export const ProfileActivePositionsTable: React.FC<Props> = ({
  className,
  delugeId,
}) => {
  const [activePosition, setActivePosition] = useState<
    AggregatedPositionModel | undefined
  >()

  const model = useMemo(() => {
    // eslint-disable-next-line @reatom/unit-naming-rule
    return reatomProfilePositionsTable({
      delugeId,
    })
  }, [delugeId])

  return (
    <Root className={className}>
      <Table
        model={model}
        rowHeight={50}
        renderBackgroundLink={({ data }) => (
          <PairLinkWrapper pair={data.pair} />
        )}
        context={{
          onOpenWalletPositions: (value: AggregatedPositionModel) =>
            setActivePosition(value),
        }}
      />
      <AnimatePresence>
        {activePosition ? (
          <WalletPositionsDialog
            position={activePosition}
            open={true}
            onOpenChange={() => setActivePosition(undefined)}
          />
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Root = styled(PageSection)`
  height: 100%;
  width: 100%;
  display: grid;

  border-radius: 0;
  border: none;
`

const Table = styled(
  AppDataTable<
    AggregatedPositionModel | WalletPositionModel,
    PositionTableColumnId,
    {
      onOpenWalletPositions?: (value: AggregatedPositionModel) => void
    }
  >,
)`
  --data-table-header-padding-inline: 12px 0;
  --data-table-row-padding-inline: 12px 0;

  height: 100%;
`

const PairLinkWrapper = styled(PairLink)`
  position: absolute;
  inset: 0;
  z-index: 0;
`
